import { notEmpty } from 'utils/typescript.utils';

import type {
  CartItem,
  CartItemInput,
} from 'services/api/restful/generated/nofomoRestApi.schemas';

import type {
  AddonType,
  LocalCartAddon,
  LocalCartItem,
  Ticket,
  TicketType,
} from './types';

export const isPaidAddon = (ticketType?: TicketType, addonType?: AddonType) =>
  ticketType === 'Addon' && addonType === 'Paid';

/** Wallet / transfer children. Uses ticketType only; account nest may omit addonType. */
export const isAddonTicket = (ticketType?: TicketType | null) =>
  ticketType === 'Addon';

export const isIncludedCatalogueChild = (ticketType?: TicketType) =>
  ticketType === 'Parking' || ticketType === 'CreditWallet';

/** Stable Addon fingerprint so the same parent catalogue with different Addon sets does not merge. */
export const getAddonSetKey = (
  children?: Array<{
    catalogueTicketId?: string | null;
    ticketType?: TicketType | null;
  } | null> | null,
) => {
  const counts = new Map<string, number>();

  children?.forEach((child) => {
    if (
      !child ||
      !isAddonTicket(child.ticketType) ||
      !child.catalogueTicketId
    ) {
      return;
    }

    counts.set(
      child.catalogueTicketId,
      (counts.get(child.catalogueTicketId) ?? 0) + 1,
    );
  });

  if (!counts.size) {
    return 'no-addons';
  }

  return Array.from(counts.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([id, quantity]) => `${id}:${quantity}`)
    .join(',');
};

/** Sort weight for sub-ticket rows: CreditWallet, Parking, Addon, then anything else. */
export const subTicketTypeOrder = (ticketType?: TicketType | null) => {
  if (ticketType === 'CreditWallet') {
    return 0;
  }

  if (ticketType === 'Parking') {
    return 1;
  }

  if (ticketType === 'Addon') {
    return 2;
  }

  return 3;
};

/** Wallet and transfer child rows: Wallet, Parking, Addon, then anything else. */
export const sortSubTicketsByType = <
  T extends { ticketType?: TicketType | null },
>(
  tickets: Array<T>,
) =>
  [...tickets].sort(
    (a, b) =>
      subTicketTypeOrder(a.ticketType) - subTicketTypeOrder(b.ticketType),
  );

export type GroupedSubTicket<T> = {
  ticket: T;
  quantity: number;
  isCollapsedAddon?: boolean;
};

/** Collapse same-catalogue Addon scannables into one row with a count. Parking / Wallet stay one row each. */
export const groupAddonSubTickets = <
  T extends {
    catalogueTicketId?: string | null;
    ticketType?: TicketType | null;
  },
>(
  tickets: Array<T>,
): Array<GroupedSubTicket<T>> => {
  const addonByCatalogueId = new Map<string, GroupedSubTicket<T>>();
  const rows: Array<GroupedSubTicket<T>> = [];

  tickets.forEach((ticket) => {
    if (!isAddonTicket(ticket.ticketType) || !ticket.catalogueTicketId) {
      rows.push({ ticket, quantity: 1 });
      return;
    }

    const existing = addonByCatalogueId.get(ticket.catalogueTicketId);
    if (existing) {
      existing.quantity += 1;
      return;
    }

    const row = { ticket, quantity: 1 };
    addonByCatalogueId.set(ticket.catalogueTicketId, row);
    rows.push(row);
  });

  return [...rows].sort(
    (a, b) =>
      subTicketTypeOrder(a.ticket.ticketType) -
      subTicketTypeOrder(b.ticket.ticketType),
  );
};

/** Merge every Addon row into one Add-Ons (count) row. Wallet / Parking stay as they are. */
export const collapseAddonRows = <T extends { ticketType?: TicketType | null }>(
  rows: Array<GroupedSubTicket<T>>,
): Array<GroupedSubTicket<T>> => {
  let addonQuantity = 0;
  let addonRowInserted = false;
  const displayRows: Array<GroupedSubTicket<T>> = [];

  rows.forEach((row) => {
    if (!isAddonTicket(row.ticket.ticketType)) {
      displayRows.push(row);
      return;
    }

    addonQuantity += row.quantity;
    if (addonRowInserted) {
      return;
    }

    addonRowInserted = true;
    displayRows.push({
      ...row,
      isCollapsedAddon: true,
    });
  });

  const addonRow = displayRows.find((row) => row.isCollapsedAddon);
  if (addonRow) {
    addonRow.quantity = addonQuantity;
  }

  return displayRows;
};

export const getPaidCatalogueAddons = (ticket?: Ticket | null) =>
  ticket?.childItems?.filter((child) =>
    isPaidAddon(child.ticketType, child.addonType),
  ) ?? [];

export const hasPaidCatalogueAddons = (ticket?: Ticket | null) =>
  getPaidCatalogueAddons(ticket).length > 0;

/** Max addons allowed for this parent: catalogue max times parent quantity. Undefined if the catalogue has no max. */
export const getAddonMaxQuantity = (
  maxQuantityPerParentItem: number | null | undefined,
  parentQty: number,
) => {
  if (typeof maxQuantityPerParentItem !== 'number') {
    return undefined;
  }

  return Math.max(0, maxQuantityPerParentItem * parentQty);
};

/** Cap the addOn quantity if it is above the parent max. Unchanged if there is no max. */
export const capAddonQuantity = (
  quantity: number,
  maxQuantityPerParentItem: number | null | undefined,
  parentQty: number,
) => {
  const maxQuantity = getAddonMaxQuantity(maxQuantityPerParentItem, parentQty);

  if (maxQuantity === undefined) {
    return quantity;
  }

  return Math.min(quantity, maxQuantity);
};

/** Keep paid addons and limit each quantity to the parent max. Quantity 0 is removed. */
export const capLocalCartAddons = (
  addons: Array<LocalCartAddon> | undefined,
  parentQty: number,
): Array<LocalCartAddon> | undefined => {
  if (!addons?.length) {
    return addons;
  }

  const cappedAddons = addons
    .filter((addon) => isPaidAddon(addon.ticketType, addon.addonType))
    .map((addon) => ({
      ...addon,
      quantity: capAddonQuantity(
        addon.quantity,
        addon.maxQuantityPerParentItem,
        parentQty,
      ),
    }))
    .filter((addon) => addon.quantity > 0);

  return cappedAddons.length ? cappedAddons : undefined;
};

/** Build addons for cart create/ReplaceItems. Cap each addon quantity to the parent max. Does not update the store. */
const mapWriteAddons = (item: LocalCartItem): CartItemInput['addons'] => {
  const addons = capLocalCartAddons(item.addons, item.quantity)?.map(
    (addon) => ({
      catalogueItemId: addon.catalogueItemId,
      quantity: addon.quantity,
    }),
  );

  return addons?.length ? addons : undefined;
};

/** Map local cart items to the cart write payload, including addons. */
export const mapLocalCartItemsToCartItemInput = (
  items: Array<LocalCartItem>,
): Array<CartItemInput> =>
  items.map((item) => ({
    catalogueItemId: item.catalogueItemId,
    quantity: item.quantity,
    bookObjects: item.bookObjectsInfo
      ?.filter(notEmpty)
      .map((bookObject) => bookObject.label),
    addons: mapWriteAddons(item),
  }));

/** Loyalty list from the local cart. Same addon under two parents stays as two rows. */
export const flattenLocalCartItemsForLoyalty = (items: Array<LocalCartItem>) =>
  items.flatMap((item) => [
    {
      catalogueItemId: item.catalogueItemId,
      quantity: item.quantity,
    },
    ...(item.addons
      ?.filter(
        (addon) =>
          isPaidAddon(addon.ticketType, addon.addonType) && addon.quantity > 0,
      )
      .map((addon) => ({
        catalogueItemId: addon.catalogueItemId,
        quantity: addon.quantity,
      })) ?? []),
  ]);

/** Loyalty list from the API cart. Uses childItems quantity as returned. Same addon under two parents stays as two rows. */
export const flattenApiCartItemsForLoyalty = (
  items: Array<CartItem> | null | undefined,
) =>
  items?.flatMap((item) => [
    {
      catalogueItemId: item.catalogueItemId,
      quantity: item.quantity,
    },
    ...(item.childItems
      ?.filter((child) => isPaidAddon(child.ticketType, child.addonType))
      .map((child) => ({
        catalogueItemId: child.catalogueItemId,
        quantity: child.quantity,
      })) ?? []),
  ]) ?? [];

/** Replace one addon on a parent nest. Quantity 0 removes it. */
export const upsertLocalCartAddon = (
  addons: Array<LocalCartAddon> | undefined,
  next: LocalCartAddon,
): Array<LocalCartAddon> | undefined => {
  const without = (addons ?? []).filter(
    (addon) => addon.catalogueItemId !== next.catalogueItemId,
  );
  const merged = next.quantity > 0 ? [...without, next] : without;

  return merged.length ? merged : undefined;
};

/** Parent ticketsInfo prices plus paid addon lines. Parking and CreditWallet are not included. */
export const getLocalCartPaidAmount = (
  items: Array<LocalCartItem>,
  getUnitAmount: (catalogueItemId: string) => number,
) =>
  items.reduce((total, item) => {
    const parentAmount = getUnitAmount(item.catalogueItemId) * item.quantity;
    const addonAmount =
      item.addons?.reduce((addonTotal, addon) => {
        if (
          !isPaidAddon(addon.ticketType, addon.addonType) ||
          addon.quantity <= 0
        ) {
          return addonTotal;
        }

        return (
          addonTotal + getUnitAmount(addon.catalogueItemId) * addon.quantity
        );
      }, 0) ?? 0;

    return total + parentAmount + addonAmount;
  }, 0);
